import React, { useRef, useEffect, useState } from 'react';
import {
  motion,
  useInView,
  useMotionValue,
  useSpring,
  useTransform,
  useMotionTemplate,
  useReducedMotion,
} from 'framer-motion';

/**
 * WhyEpicurea — single-column editorial ledger.
 *
 * MOTION PHILOSOPHY:
 *   • Spring physics, not duration tweens. Each element has real weight.
 *   • Each row enters as ONE connected gesture — numeral leads, before line
 *     follows, after line lands with a tiny overshoot, underline draws last.
 *     Internal stagger is small (~80-200ms) so the parts read as one motion,
 *     not four separate fades.
 *   • Per-row base delays are INTENTIONALLY irregular (not 0, 0.1, 0.2, 0.3)
 *     so the rhythm feels hand-set.
 *   • Asymmetric timing — confident springs (relatively high stiffness on the
 *     headline element, lower stiffness + higher mass on the underline) give a
 *     "quick start, long settle" without anything ever moving at constant speed.
 *   • Cursor tilt has HEAVY/LAGGY follow (low stiffness, high mass) so each row
 *     feels like a physical card, not a planar overlay 1:1-mapped to the cursor.
 *   • The ONE decorative motion is the emerald underline drawing under the
 *     'after' line — and it corresponds to the content being emphasized. No
 *     sparks, no glows, no particles.
 *   • prefers-reduced-motion → quiet static fallback, no tilt.
 */

// ---------- Content ----------
const ENTRIES = [
  {
    before: 'Quarterly P&L review — variances surface months after the recovery window closes.',
    after:  'Daily 3-way reconciliation (POS ↔ Tally ↔ Bank), down to the individual bill.',
  },
  {
    before: 'No dispute workflow — write-back losses from Swiggy / Zomato just accepted.',
    after:  'Auto-flags wrong commission tiers, promo-leakage, cancelled-but-paid orders.',
  },
  {
    before: 'Generic books. Nothing the owner can read with their morning chai.',
    after:  'Weekly Brief every Monday. Monthly Detailed Report on the 7th. Owner-grade.',
  },
  {
    before: 'A red row with no story. Owner left to guess the cause.',
    after:  'AI variance explainer — every red row gets a one-line root cause.',
  },
];

const ROMAN = ['i', 'ii', 'iii', 'iv'];

// Intentionally irregular base delays — not a uniform increment.
// Intervals: 0.18, 0.16, 0.22 — slight push-and-rest rhythm.
const ROW_BASE_DELAY = [0.00, 0.18, 0.34, 0.56];

// ---------- Palette (matched to LeaksGrid — "Where Margin Slips") ----------
const C_BG_TOP      = '#F4ECDB';                  // warm cream — top of gradient
const C_BG_BOT      = '#ECE2C7';                  // warm cream — bottom of gradient
const C_INK         = '#28282B';                  // soft charcoal ink ('after' + headline)
const C_INK_MUTED   = '#5A4A20';                  // warm brown ('before' / sub-headline)
const C_NUMERAL     = '#C9A66B';                  // gold (matches LeaksGrid numerals)
const C_EMERALD     = '#7A5F2E';                  // dark gold (matches LeaksGrid squiggle stroke)
const C_HEADLINE_HI = '#7A5F2E';                  // dark gold italic accent on the headline

// ============================================================
// useCanHover — pointer-fine + hover-hover detection
// ============================================================
const useCanHover = () => {
  const [canHover, setCanHover] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const onChange = () => setCanHover(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return canHover;
};

// ============================================================
// LedgerRow — one connected gesture per row
// ============================================================
const LedgerRow = ({ entry, index, reduced, canHover }) => {
  const rowRef = useRef(null);
  const tiltRef = useRef(null);
  const inView = useInView(rowRef, { once: true, amount: 0.45, margin: '0px 0px -8% 0px' });
  const baseDelay = ROW_BASE_DELAY[index] ?? 0;

  // ---- Spring presets (each element has its own weight) ----
  // Numeral: light, settles quickly. ζ ≈ 1.2 (overdamped, no overshoot)
  const numeralSpring = { type: 'spring', stiffness: 95,  damping: 20, mass: 0.7 };
  // Before line: gentle drift, slightly heavier. ζ ≈ 1.3
  const beforeSpring  = { type: 'spring', stiffness: 85,  damping: 22, mass: 0.85 };
  // After line: the headline of the row — underdamped so it overshoots subtly
  // before settling. ζ ≈ 0.68. The follow-through is the "real object" feel.
  const afterSpring   = { type: 'spring', stiffness: 115, damping: 14, mass: 0.95 };
  // Underline: drawn by hand — near-critical damping, a hair of settle at end.
  // ζ ≈ 1.04 → barely-perceptible overshoot. Use a spring (not a tween) so the
  // draw decelerates organically rather than mechanically.
  const underlineSpring = { type: 'spring', stiffness: 95, damping: 17, mass: 0.7 };

  // ---- Cursor tilt — heavy/laggy follow ----
  // Low stiffness + high mass = the card visibly lags behind the cursor.
  // ζ = 18 / (2 * sqrt(55 * 1.2)) ≈ 1.11 → smooth, no overshoot.
  const rxMV = useMotionValue(0);
  const ryMV = useMotionValue(0);
  const rxSpring = useSpring(rxMV, { stiffness: 55, damping: 18, mass: 1.2 });
  const rySpring = useSpring(ryMV, { stiffness: 55, damping: 18, mass: 1.2 });

  // Shadow shifts with tilt — anchors the depth perception
  const shadowX     = useTransform(rySpring, [-6, 6], [-10, 10]);
  const shadowY     = useTransform(rxSpring, [-4, 4], [14, -2]);
  const shadowBlur  = useTransform([rxSpring, rySpring], ([rx, ry]) =>
    24 + Math.abs(rx) * 1.2 + Math.abs(ry) * 1.2
  );
  const shadowAlpha = useTransform([rxSpring, rySpring], ([rx, ry]) =>
    0.08 + (Math.abs(rx) + Math.abs(ry)) * 0.013
  );
  const boxShadow = useMotionTemplate`${shadowX}px ${shadowY}px ${shadowBlur}px -8px rgba(40, 40, 43, ${shadowAlpha})`;

  const tiltActive = canHover && !reduced;

  const handleMove = (e) => {
    if (!tiltActive || !tiltRef.current) return;
    const r = tiltRef.current.getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width - 0.5;
    const ny = (e.clientY - r.top)  / r.height - 0.5;
    ryMV.set(nx * 12);  // ±6° on Y
    rxMV.set(-ny * 8);  // ±4° on X
  };
  const handleLeave = () => {
    rxMV.set(0);
    ryMV.set(0);
  };

  return (
    <div ref={rowRef} className="relative" data-testid={`ledger-row-${index}`}>
      <div style={{ perspective: tiltActive ? 1200 : 'none' }}>
        <motion.div
          ref={tiltRef}
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
          style={tiltActive ? {
            rotateX: rxSpring,
            rotateY: rySpring,
            transformStyle: 'preserve-3d',
            boxShadow,
            borderRadius: 14,
          } : undefined}
          className="grid grid-cols-[auto_1fr] gap-x-5 md:gap-x-10 py-9 md:py-14 px-2 md:px-5"
        >
          {/* Roman numeral — leads the gesture */}
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 10 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={reduced ? { duration: 0.4 } : { ...numeralSpring, delay: baseDelay }}
            className="pt-1.5 md:pt-2 select-none"
            style={{
              fontFamily: "'Fraunces', 'Playfair Display', Georgia, serif",
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: 'clamp(20px, 1.8vw, 26px)',
              color: C_NUMERAL,
              minWidth: '2.4rem',
              letterSpacing: '0.01em',
            }}
            aria-hidden="true"
          >
            {ROMAN[index]}.
          </motion.div>

          {/* Content stack — before / after / underline (one connected gesture) */}
          <div>
            {/* Before — muted, drifts gently from the left */}
            <motion.p
              initial={reduced ? false : { opacity: 0, x: -8 }}
              animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -8 }}
              transition={reduced ? { duration: 0.4 } : { ...beforeSpring, delay: baseDelay + 0.08 }}
              className="text-[15px] md:text-[16px] leading-relaxed mb-5 md:mb-6"
              style={{
                color: C_INK_MUTED,
                fontFamily: "'Manrope', system-ui, sans-serif",
                fontWeight: 400,
              }}
            >
              {entry.before}
            </motion.p>

            {/* After — confident serif, lands with subtle overshoot-and-settle */}
            <motion.p
              initial={reduced ? false : { opacity: 0, y: 14 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
              transition={reduced ? { duration: 0.45 } : { ...afterSpring, delay: baseDelay + 0.22 }}
              className="text-[22px] md:text-[28px] leading-[1.22] tracking-tight"
              style={{
                color: C_INK,
                fontFamily: "'Playfair Display', 'Fraunces', Georgia, serif",
                fontWeight: 500,
              }}
            >
              {entry.after}
            </motion.p>

            {/* Underline — hand-drawn, settles last as a small punctuation */}
            <motion.div
              initial={reduced ? false : { scaleX: 0 }}
              animate={inView ? { scaleX: 1 } : { scaleX: 0 }}
              transition={reduced ? { duration: 0.5 } : { ...underlineSpring, delay: baseDelay + 0.52 }}
              className="mt-5 md:mt-7"
              style={{
                width: 'clamp(96px, 16vw, 152px)',
                height: '2px',
                background: C_EMERALD,
                transformOrigin: 'left center',
                borderRadius: 1,
              }}
              aria-hidden="true"
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// ============================================================
// Section
// ============================================================
export const WhyEpicurea = () => {
  const reduced = useReducedMotion();
  const canHover = useCanHover();

  return (
    <section
      id="why"
      data-testid="landing-why"
      className="relative py-40 md:py-56 overflow-hidden"
      style={{ background: `linear-gradient(180deg, ${C_BG_TOP} 0%, ${C_BG_BOT} 100%)` }}
    >
      {/* TOP feather — blends down from the dark ReportPreviews above */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 left-0 right-0 h-40 z-[1]"
        style={{ background: `linear-gradient(180deg, #0F0B0A 0%, ${C_BG_TOP} 100%)` }}
      />

      {/* BOTTOM feather — fades to dark for the next section */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-0 right-0 h-40"
        style={{ background: `linear-gradient(0deg, #0F0B0A 0%, ${C_BG_BOT} 100%)` }}
      />

      {/* Paper grain — same recipe as LeaksGrid (multiply blend on warm cream) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.12] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="relative mx-auto max-w-3xl px-6 md:px-10">
        {/* Headline + sub */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-120px' }}
          transition={
            reduced
              ? { duration: 0.5 }
              : { type: 'spring', stiffness: 75, damping: 19, mass: 0.95 }
          }
          className="mb-20 md:mb-28"
        >
          <h2
            className="text-4xl md:text-6xl tracking-tight leading-[1.04]"
            style={{
              color: C_INK,
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 500,
            }}
          >
            Most accountants close
            <span
              className="block italic"
              style={{ color: C_HEADLINE_HI, fontWeight: 500 }}
            >
              your books.
            </span>
            <span className="block">We close the gaps.</span>
          </h2>
          <p
            className="mt-7 md:mt-9 text-lg md:text-xl italic leading-relaxed max-w-2xl"
            style={{
              color: C_INK_MUTED,
              fontFamily: "'Fraunces', 'Playfair Display', Georgia, serif",
              fontWeight: 400,
            }}
          >
            The difference is not effort. It is where you look, and how often.
          </p>
        </motion.div>

        {/* Ledger rows */}
        <div className="divide-y" style={{ borderColor: 'rgba(122, 95, 46, 0.18)' }}>
          {ENTRIES.map((entry, i) => (
            <LedgerRow
              key={i}
              entry={entry}
              index={i}
              reduced={!!reduced}
              canHover={canHover}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyEpicurea;
